"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Loader } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function RequestInformationSection() {
  return <section id="solicita-información" className="bg-background py-48">
      <div className="container">
        <FormProvider className="w-full" formId="info_request_form_olive_grove_to_bottle" sectionName="request-information-section" defaultValues={{
        nombre: "",
        email: "",
        telefono: "",
        empresa: "",
        mensaje: ""
      }} validationRules={{
        nombre: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        telefono: {
          pattern: "phone"
        },
        empresa: {
          maxLength: 100
        },
        mensaje: {
          required: true,
          minLength: 10
        }
      }} submitText="Enviar Solicitud" successMessage="¡Gracias! Hemos recibido su solicitud y nos pondremos en contacto con usted pronto." errorMessage="Error al enviar el formulario. Por favor, inténtelo de nuevo.">
          <RequestInformationForm />
        </FormProvider>
      </div>
    </section>;
}
function RequestInformationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="bg-transparent border-none">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
              <svg className="size-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-serif font-semibold text-foreground mb-2">Solicitud Enviada</h3>
              <p className="text-muted-foreground">{successMessage}</p>
            </div>
            <Button onClick={resetForm} className="btn mt-2">
              Enviar Otra Solicitud
            </Button>
          </div>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-transparent border-none">
      <CardHeader className="text-center pb-6">
        <CardTitle className="text-2xl font-serif text-foreground">¿Interesado en nuestra producción?</CardTitle>
        <CardDescription className="text-base text-muted-foreground mt-2">
          Complete el formulario y nos pondremos en contacto con usted para proporcionarle toda la información que necesite.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <FormField control={control} name="nombre" rules={{
            required: "El nombre completo es obligatorio",
            minLength: {
              value: 2,
              message: "Mínimo 2 caracteres"
            },
            maxLength: {
              value: 100,
              message: "Máximo 100 caracteres"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Nombre Completo</FormLabel>
                  <FormControl>
                    <Input className="input" placeholder="Juan Pérez" {...field} />
                  </FormControl>
                  <FormDescription>Indique su nombre y apellidos.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "El correo electrónico es obligatorio",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Formato de correo inválido"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Correo Electrónico</FormLabel>
                  <FormControl>
                    <Input className="input" type="email" placeholder="juan@example.com" {...field} />
                  </FormControl>
                  <FormDescription>Utilizaremos este email para responder a su consulta.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="telefono" rules={{
            pattern: {
              value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
              message: "Formato de teléfono inválido"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Teléfono</FormLabel>
                  <FormControl>
                    <Input className="input" type="tel" placeholder="+31 6 1234 5678" {...field} />
                  </FormControl>
                  <FormDescription>Opcional: si prefiere que le contactemos por teléfono.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="empresa" rules={{
            maxLength: {
              value: 100,
              message: "Máximo 100 caracteres"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Empresa / Tienda</FormLabel>
                  <FormControl>
                    <Input className="input" placeholder="Delicatessen La Olive" {...field} />
                  </FormControl>
                  <FormDescription>Indique el nombre de su negocio si es una consulta B2B.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="mensaje" rules={{
            required: "El mensaje es obligatorio",
            minLength: {
              value: 10,
              message: "Mínimo 10 caracteres"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Mensaje / Consulta</FormLabel>
                  <FormControl>
                    <textarea className="border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 placeholder:text-muted-foreground flex min-h-[120px] w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none" placeholder="Escriba aquí su pregunta o solicitud de información..." {...field} />
                  </FormControl>
                  <FormDescription>Cuéntenos qué le gustaría saber sobre nuestro proceso o productos.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="rounded-md bg-primary/10 border border-primary/20 p-4">
                <p className="text-sm text-primary">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="btn w-full">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Enviando...
                </> : 'Enviar Solicitud'}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}